import { useEffect, useRef, useState } from 'react';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { getApplicationEnv } from '../api';
import { createPortal } from 'react-dom';
import { colors, cardStyle } from '../theme';
import { EnvVarsEditor, type EnvVarRow } from './EnvVarsEditor';
import type { ApplicationSummary } from '../types';

/**
 * Redeploy dialog: change environment variables and/or rebuild from the app's
 * own repository.
 *
 * The two options are presented together because they answer the same
 * question ("make my change live") with different scopes, and because a
 * Coolify environment variable only reaches a container at deploy time — so
 * editing config without redeploying does nothing, which is a genuinely
 * confusing trap to leave in a UI.
 *
 * Existing values are deliberately NOT prefilled. `GET /env` redacts secrets,
 * so prefilling would show `<redacted — set by the platform>` in an editable
 * box and a careless save would write that literal string over a real
 * credential. Blank rows are additive: anything left out is untouched.
 */
export function RedeployApplicationDialog({
  app,
  busy,
  stage,
  error,
  onCancel,
  onConfirm,
}: {
  app: ApplicationSummary;
  busy: boolean;
  /** Workflow status while running, e.g. BUILDING_IMAGE — worth surfacing,
   * since a rebuild takes minutes and silence reads as a hang. */
  stage: string | null;
  error: string | null;
  onCancel: () => void;
  onConfirm: (payload: {
    envVars?: Record<string, string>;
    removeEnvKeys?: string[];
    rebuildFromRepo: boolean;
  }) => void;
}) {
  const identityApi = useApi(identityApiRef);
  const [rows, setRows] = useState<EnvVarRow[]>([]);
  const [rebuild, setRebuild] = useState(false);
  // Current variable NAMES only — values are never prefilled (see the header
  // comment): the box below is "type a new value to replace it".
  const [existingKeys, setExistingKeys] = useState<string[] | null>(null);
  const [replacements, setReplacements] = useState<Record<string, string>>({});
  const [removals, setRemovals] = useState<Set<string>>(new Set());
  const isCompose = app.type === 'Compose App';

  useEffect(() => {
    let cancelled = false;
    getApplicationEnv(identityApi, app.id)
      .then((env) => {
        if (!cancelled) setExistingKeys(Object.keys(env).sort());
      })
      .catch(() => {
        if (!cancelled) setExistingKeys([]);
      });
    return () => {
      cancelled = true;
    };
  }, [identityApi, app.id]);

  // Platform-managed tags (owner, source repo, app type…) are not editable.
  const editableKeys = (existingKeys ?? []).filter((k) => !k.startsWith('MUMZWORLD_'));
  const hiddenCount = (existingKeys?.length ?? 0) - editableKeys.length;
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    confirmRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  const envVars = rows.reduce<Record<string, string>>((acc, row) => {
    const key = row.key.trim();
    if (key) acc[key] = row.value;
    return acc;
  }, {});
  // Typed replacements for existing variables win over nothing, but a variable
  // marked for removal is never also written.
  for (const [key, value] of Object.entries(replacements)) {
    if (value !== '' && !removals.has(key)) envVars[key] = value;
  }
  const removeEnvKeys = [...removals];
  const envCount = Object.keys(envVars).length + removeEnvKeys.length;
  // A redeploy that changes nothing still has a use — restarting a container —
  // so this is never disabled for lack of input, only while busy.
  const summary = rebuild
    ? envCount > 0
      ? `Rebuild from repo and apply ${envCount} variable${envCount === 1 ? '' : 's'}`
      : 'Rebuild from repo and redeploy'
    : envCount > 0
      ? `Apply ${envCount} change${envCount === 1 ? '' : 's'} and redeploy`
      : 'Redeploy the current image';

  return createPortal(
    <>
      <div
        onClick={() => !busy && onCancel()}
        style={{ position: 'fixed', inset: 0, background: 'rgba(16, 24, 40, 0.45)', zIndex: 200 }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Redeploy ${app.name}`}
        style={{
          ...cardStyle,
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'calc(100% - 32px)',
          maxWidth: 560,
          maxHeight: 'calc(100vh - 64px)',
          overflowY: 'auto',
          padding: 24,
          zIndex: 201,
        }}
      >
        <h2 style={{ fontSize: 17, fontWeight: 700, color: colors.text, margin: '0 0 6px' }}>
          Edit environment &amp; redeploy — {app.name}
        </h2>
        <p style={{ fontSize: 14, color: colors.textMuted, margin: '0 0 20px', lineHeight: 1.5 }}>
          The app briefly restarts. Its URL and data are unchanged.
        </p>

        {!isCompose && (
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 14, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={rebuild}
              disabled={busy}
              onChange={(e) => setRebuild(e.target.checked)}
              aria-label="Rebuild from repository"
              style={{ marginTop: 3 }}
            />
            <span>
              <span style={{ fontWeight: 600, color: colors.text }}>Rebuild from repository</span>
              <span style={{ display: 'block', color: colors.textMuted, marginTop: 2 }}>
                Builds the latest commit from this app&apos;s repo. Leave off to redeploy the current
                image — enough to apply an environment change, but it will not pick up code changes.
              </span>
            </span>
          </label>
        </div>
        )}

        <div style={{ marginBottom: 4 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: colors.text }}>Current variables</span>
          <p style={{ fontSize: 13, color: colors.textMuted, margin: '4px 0 10px', lineHeight: 1.5 }}>
            Values are hidden. Type a new value to replace one, or tick Remove to delete it.
            {hiddenCount > 0 && ` ${hiddenCount} platform-managed variable${hiddenCount === 1 ? '' : 's'} not shown.`}
          </p>
          {existingKeys === null && <div style={{ fontSize: 13, color: colors.textMuted }}>Loading…</div>}
          {existingKeys !== null && editableKeys.length === 0 && (
            <div style={{ fontSize: 13, color: colors.textMuted }}>No editable variables yet.</div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
            {editableKeys.map((key) => (
              <div key={key} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <code style={{ flex: '0 0 38%', fontSize: 12, color: colors.text, overflowWrap: 'anywhere' }}>{key}</code>
                <input
                  type="password"
                  disabled={busy || removals.has(key)}
                  placeholder={removals.has(key) ? 'will be removed' : '•••••• unchanged — type to replace'}
                  value={replacements[key] ?? ''}
                  onChange={(e) => setReplacements({ ...replacements, [key]: e.target.value })}
                  aria-label={`New value for ${key}`}
                  style={{
                    flex: 1,
                    padding: '8px 10px',
                    borderRadius: 6,
                    border: `1px solid ${colors.border}`,
                    fontSize: 13,
                    background: colors.surface,
                    color: colors.text,
                  }}
                />
                <label style={{ fontSize: 12, color: colors.danger, whiteSpace: 'nowrap', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    disabled={busy}
                    checked={removals.has(key)}
                    onChange={(e) => {
                      const next = new Set(removals);
                      if (e.target.checked) next.add(key);
                      else next.delete(key);
                      setRemovals(next);
                    }}
                    aria-label={`Remove ${key}`}
                  />{' '}
                  Remove
                </label>
              </div>
            ))}
          </div>
          <span style={{ fontSize: 13, fontWeight: 600, color: colors.text }}>Add variables</span>
          <p style={{ fontSize: 13, color: colors.textMuted, margin: '4px 0 10px', lineHeight: 1.5 }}>
            New variables are added; anything you don&apos;t touch is left alone.
          </p>
          <EnvVarsEditor rows={rows} onChange={setRows} />
        </div>

        {error && (
          <div role="alert" style={{ fontSize: 13, color: colors.danger, margin: '16px 0 0' }}>
            {error}
          </div>
        )}

        {busy && stage && (
          <div style={{ fontSize: 13, color: colors.textMuted, marginTop: 16 }}>
            {stage.replace(/_/g, ' ').toLowerCase()}…
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            style={{
              padding: '9px 16px',
              borderRadius: 8,
              border: `1px solid ${colors.border}`,
              background: colors.surface,
              color: colors.text,
              fontSize: 14,
              fontWeight: 600,
              cursor: busy ? 'default' : 'pointer',
              opacity: busy ? 0.6 : 1,
            }}
          >
            Cancel
          </button>
          <button
            ref={confirmRef}
            type="button"
            aria-label={`Confirm redeploy ${app.name}`}
            disabled={busy}
            onClick={() => onConfirm({
                envVars: Object.keys(envVars).length > 0 ? envVars : undefined,
                removeEnvKeys: removeEnvKeys.length > 0 ? removeEnvKeys : undefined,
                rebuildFromRepo: rebuild && !isCompose,
              })}
            style={{
              padding: '9px 16px',
              borderRadius: 8,
              border: 'none',
              background: busy ? colors.borderLight : colors.primary,
              color: busy ? colors.textMuted : '#fff',
              fontSize: 14,
              fontWeight: 600,
              cursor: busy ? 'default' : 'pointer',
            }}
          >
            {busy ? 'Redeploying…' : summary}
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
}
